import { Injectable } from '@angular/core';
import { AbcCore, CompareHelper, ConvertHelper } from '@barthauer/abc/core';
import { ProfileInfo, ProfileService } from '@barthauer/abc/core/authentication';
import {
  CacheContext,
  CacheDataSet,
  CacheProgress,
  CacheStorageAdapter,
  CacheStorageDataSet,
  EvaluateDefaultValues,
  EvaluateValues,
  ObjectData,
  ObjectKey,
  Query,
  QueryColumn,
  QueryObjectsModel,
  QueryPathModel,
  ReadValidateModel,
  TargetColumnValue,
  ValueDefinition,
} from '@barthauer/abc/core/data';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { _AbcGridComponent } from '@barthauer/abc/ui-components';
import { from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, concatMap } from 'rxjs/operators';
import { ConfigService } from '../configuration/config.service';
import { LayoutDocument, SpecialType } from '../core/models/layout.model';
import { ReadLayoutModel, ReadNavigationModel } from '../core/models/query.model';
import { ArenaCacheSettings } from '../core/models/setting.model';
import { NavigationDocument } from '../core/navigation/navigation.model';
import { NavigationLayoutViewModel, NavigationViewModel } from '../core/navigation/navigation.viewModel';
import { LayoutColumnViewModel, LayoutViewModel } from '../core/viewModels/layout.viewModel';

export interface GridQueryDefinition {
  objectType: string;
  oPath: string;
  visibleColumns: LayoutColumnViewModel[];
}

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private readNavigationDataServiceUri: string;
  private readLayoutDataServiceUri: string;
  private navigationsViewModelData: {} = {};
  private layoutsViewModelData: {} = {};

  private cacheDataSet: CacheDataSet;
  private readonly tableNameDefinition = 'definition';
  private readonly tableNameGridData = 'griddata';
  private readonly tableNameFormData = 'formdata';

  public arenaNames: string[];
  public cacheSettings: ArenaCacheSettings;

  constructor(
    public abcCore: AbcCore,
    private configService: ConfigService,
    private profileService: ProfileService,
  ) {
    this.readNavigationDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.ReadNavigation;
    this.readLayoutDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.ReadLayout;

    this.cacheDataSet = new CacheDataSet('Arena', null, [
      this.tableNameDefinition,
      this.tableNameGridData,
      this.tableNameFormData,
    ]);
    this.cacheDataSet.cacheMethod = (ctx) => this.downloadDataToCache(ctx, this.cacheDataSet, this.arenaNames);
    this.abcCore.cache.storage.items.push(this.cacheDataSet);
  }

  public async getNavigation(
    arenaName: string,
    prepareData?: (data: NavigationDocument) => Promise<void>,
  ): Promise<NavigationViewModel> {
    const data = await this.profileService.getCurrentProfileSettings().toPromise();
    const navViewModelKey = arenaName + (data ? '-' + data.id : '');
    const nvm = this.navigationsViewModelData[navViewModelKey] as NavigationViewModel;
    if (nvm) {
      return nvm;
    } else {
      const data = ConvertHelper.arrayBufferToObject(await this.readNavigation(arenaName).toPromise());
      if (data) {
        const navDoc = new NavigationDocument(data);
        if (prepareData) {
          await prepareData(navDoc);
        }
        const navViewDoc = new NavigationViewModel(navDoc);
        this.navigationsViewModelData[navViewModelKey] = navViewDoc;
        return navViewDoc;
      }
      return null;
    }
  }

  private readNavigation(arenaName: string): Observable<ArrayBuffer> {
    if (CacheStorageAdapter.useOfflineMode) {
      return from(this.tryLoadNavigationFromCache(arenaName));
    }
    const query = new ReadNavigationModel();
    query.ArenaName = arenaName;
    return this.abcCore.data.executeWebApiPOSTMethod(this.readNavigationDataServiceUri, query).pipe(
      catchError((_) => {
        return from(this.tryLoadNavigationFromCache(arenaName));
      }),
    );
  }

  public async getLayout(arenaName: string, layoutId: string): Promise<LayoutViewModel> {
    if (!this.layoutsViewModelData[arenaName]) this.layoutsViewModelData[arenaName] = {};
    let lvm: LayoutViewModel = this.layoutsViewModelData[arenaName][layoutId];
    if (!lvm) {
      const awaitedReadLayout = await this.readLayout(arenaName, layoutId).toPromise();
      const layout: LayoutDocument = ConvertHelper.arrayBufferToObject(awaitedReadLayout);
      if (!CompareHelper.isEmptyObject(layout)) {
        lvm = new LayoutViewModel(layoutId, layout);
        this.layoutsViewModelData[arenaName][layoutId] = lvm;
        lvm.ensureAllColumnsLoaded();
      }
    }
    return lvm;
  }

  private readLayout(arenaName: string, layoutId: string): Observable<ArrayBuffer> {
    if (CacheStorageAdapter.useOfflineMode) {
      return from(this.tryLoadLayoutFromCache(arenaName, layoutId));
    }
    const query = new ReadLayoutModel();
    query.ArenaName = arenaName;
    query.LayoutId = layoutId;
    return this.abcCore.data.executeWebApiPOSTMethod(this.readLayoutDataServiceUri, query).pipe(
      catchError((_) => {
        return from(this.tryLoadLayoutFromCache(arenaName, layoutId));
      }),
    );
  }

  public readObjects(query: QueryObjectsModel, idList?: string[]): Observable<ArrayBuffer> {
    return this.abcCore.query.readObjects(query, idList);
  }

  public lastDataError: any;

  public readGridObjects(
    arenaName: string,
    layout: LayoutViewModel,
    additionalOPaths: string[],
    grid: _AbcGridComponent,
    idList?: string[],
    isAddedColumn?: boolean,
    parameterValues?: ParameterValue[],
  ): Observable<ArrayBuffer> {
    const filter: string = additionalOPaths?.length ? '(' + additionalOPaths.join(') AND (') + ')' : null;
    if (CacheStorageAdapter.useOfflineMode && layout instanceof LayoutViewModel) {
      return from(this.tryLoadGridDataFromCache(arenaName, layout.id, filter));
    }
    return this.readObjects(
      this.createGridObjectsQuery(layout, additionalOPaths, grid, isAddedColumn, parameterValues),
      idList,
    ).pipe(
      catchError((_) => {
        this.lastDataError = _;
        if (layout instanceof LayoutViewModel) {
          return from(this.tryLoadGridDataFromCache(arenaName, layout.id, filter));
        } else {
          throwError(_);
        }
      }),
    );
  }

  public linkOPathExpressions(mainOPath: string, additionalOPaths: string[], operator: string): string {
    let opath = mainOPath;
    if (additionalOPaths?.length) {
      const filters: string[] = [];
      if (opath && opath != '') {
        filters.push(opath);
      }
      for (let filter of additionalOPaths) {
        if (filter && filter != '') {
          filters.push(filter);
        }
      }
      if (filters.length === 1) {
        opath = filters[0];
      } else if (filters.length > 1) {
        for (let i = 0; i < filters.length; i++) {
          filters[i] = '(' + filters[i] + ')';
        }
        opath = '(' + filters.join(') ' + operator + ' (') + ')';
      }
    }
    return opath;
  }

  private createGridObjectsQuery(
    layout: LayoutViewModel,
    additionalOPaths: string[],
    grid: _AbcGridComponent,
    isAddedColumn?: boolean,
    parameterValues?: ParameterValue[],
  ): QueryObjectsModel {
    const opath: string = this.linkOPathExpressions(layout.oPath, additionalOPaths, 'AND');
    const gridColumns = grid?.columns;
    if (gridColumns && gridColumns.length && !isAddedColumn) {
      gridColumns?.forEach((col) => {
        if (col.visible) {
          const c = layout.gridColumns.find((_) => _.dataField === col.dataField);
          if (c) {
            if (grid?.instance) {
              c.visibleIndex = grid.instance.columnOption(col.dataField, 'visibleIndex');
              c.visible = grid.instance.columnOption(col.dataField, 'visible');
            } else {
              c.visibleIndex = col.visibleIndex;
              c.visible = col.visible;
            }
          }
        }
      });
    }
    return this.abcCore.data.createSimpleQuery(
      '',
      layout.objectType,
      opath,
      (layout.visibleGridColumns ?? layout.columns).map((t) => {
        if (t?.oPath) {
          const column = new QueryColumn();
          column.Name = t.dataField;
          column.OPath = t.oPath;
          if (t.sortOrder) {
            column.SortOrder = t.sortOrder;
          }
          return column;
        }
      }),
      parameterValues,
    );
  }

  public readDataFormObjects(arenaName: string, layout: LayoutViewModel, idList?: string[]): Observable<ArrayBuffer> {
    if (CacheStorageAdapter.useOfflineMode) {
      return from(this.tryLoadFormDataFromCache(arenaName, layout.id, idList));
    }
    return this.readObjects(this.createDataFormObjectsQuery(layout, null), idList).pipe(
      catchError((_) => {
        this.lastDataError = _;
        return from(this.tryLoadFormDataFromCache(arenaName, layout.id, idList));
      }),
    );
  }

  public validateOpath(parentObjectTypeName: string, subLayoutViewOpath: string) {
    const readValidateModel = new ReadValidateModel();
    readValidateModel.ObjectTypeName = parentObjectTypeName;
    readValidateModel.OPath = ['', subLayoutViewOpath];
    return this.abcCore.data.executeValidateQuery(readValidateModel);
  }

  public reversePathToExistQuery(objectType: string | number, oPath: string, idParameterName: string) {
    const queryPathModel: QueryPathModel = new QueryPathModel();
    queryPathModel.ObjectType = objectType;
    queryPathModel.OPath = oPath;
    queryPathModel.IdParameterName = idParameterName;
    return this.abcCore.data.executeReversePathToExistsQuery(queryPathModel);
  }

  public evaluateValues(
    objectType: string,
    valueDefinitions: ValueDefinition[],
    targetColumns: TargetColumnValue[],
    id?: string,
  ): Observable<ArrayBuffer> {
    const queryEvaluateValues: EvaluateValues = new EvaluateValues();
    const objectData = new ObjectData();
    objectData.ObjectKey = new ObjectKey();
    objectData.ObjectKey.ObjectType = objectType;

    if (id) {
      objectData.ObjectKey.Id = id;
    }
    objectData.TargetColumns = targetColumns;
    queryEvaluateValues.Object = objectData;
    queryEvaluateValues.Values = valueDefinitions;

    return this.abcCore.data.evaluateValues(queryEvaluateValues);
  }

  public evaluateDefaultValues(
    objectType: string,
    targetColumns: TargetColumnValue[],
    contextFilterExpressions: string[],
    id?: string,
  ): Observable<ArrayBuffer> {
    const queryEvaluateValues: EvaluateDefaultValues = new EvaluateDefaultValues();
    const objectData = new ObjectData();
    objectData.ObjectKey = new ObjectKey();
    objectData.ObjectKey.ObjectType = objectType;

    if (id) {
      objectData.ObjectKey.Id = id;
    }
    objectData.TargetColumns = targetColumns;
    queryEvaluateValues.Object = objectData;
    queryEvaluateValues.ContextFilterExpressions = contextFilterExpressions;

    return this.abcCore.data.evaluateDefaultValues(queryEvaluateValues);
  }

  private createDataFormObjectsQueryForCaching(
    layout: LayoutViewModel,
    referencedNavLayouts: NavigationLayoutViewModel[],
    idColumnName?: string,
  ): QueryObjectsModel {
    const query: QueryObjectsModel = this.createDataFormObjectsQuery(layout, idColumnName);

    let opath: string = layout.oPath;
    if (referencedNavLayouts && referencedNavLayouts.length > 0) {
      const stringNavLayoutFilters: string[] = referencedNavLayouts
        .filter((l) => l.nodeFilter)
        .map((l) => l.nodeFilter);
      if (stringNavLayoutFilters.length > 0) {
        opath = this.linkOPathExpressions(null, stringNavLayoutFilters, 'OR');
      }
    }

    query.ObjectQueries[0].OPath = opath;
    return query;
  }

  private createDataFormObjectsQuery(layout: LayoutViewModel, idColumnName?: string): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const dataFormObjectsQuery: Query = new Query();
    const dataFormColumns: QueryColumn[] = [];
    dataFormObjectsQuery.ObjectType = layout.objectType;
    layout.columnsObjectMap.forEach((columns, key) => {
      columns.forEach((column) => {
        if (column.LayoutColumn) {
          if (column.LayoutColumn?.SpecialType !== SpecialType.PropertiesEditor) {
            const queryColumn = new QueryColumn();
            const opath = column.LayoutColumn.OPath;
            queryColumn.Name = column.Key;
            queryColumn.OPath = opath;
            if (opath) {
              //To get Ids of isRef selected columns
              if (column.LayoutColumn.IsRef) {
                const queryColumnIdRef = new QueryColumn();
                queryColumnIdRef.Name = 'DisplayText:' + column.Key;
                queryColumnIdRef.OPath = 'ref:' + opath;
                dataFormColumns.push(queryColumnIdRef);
              }
              dataFormColumns.push(queryColumn);
            }
          }
          // add column Id to the readObject request when we have specialType = "Comment"
          if (column.LayoutColumn?.SpecialType === SpecialType.Comment && column.LayoutColumn?.isEditableComment) {
            const pos = column.LayoutColumn.OPath.indexOf('Text');
            const queryColumnCommentId = new QueryColumn();
            const opathId = column.LayoutColumn.OPath?.slice(0, pos) + 'Id';
            queryColumnCommentId.Name = '_' + column.Key;
            queryColumnCommentId.OPath = opathId;
            if (opathId) {
              dataFormColumns.push(queryColumnCommentId);
            }
            const queryColumnComment = new QueryColumn();
            const opath = column.LayoutColumn.OPath?.slice(0, pos) + 'CaseNull(Comment, Text)';
            queryColumnComment.Name = '__' + column.Key;
            queryColumnComment.OPath = opath;
            queryColumnComment.Format = 'Html';
            if (opath) {
              dataFormColumns.push(queryColumnComment);
            }
          }
        }
      });
    });
    if (idColumnName) {
      dataFormColumns.push({
        Name: idColumnName,
        OPath: 'Id',
      });
    }

    dataFormObjectsQuery.Columns = dataFormColumns;
    queryDocument.ObjectQueries = [dataFormObjectsQuery];
    return queryDocument;
  }

  private filterArenaNavigationDocument(): (data: NavigationDocument) => Promise<void> {
    if (this.cacheSettings && this.cacheSettings.modifyNavigationDocument) {
      return async (data: NavigationDocument) => {
        await this.cacheSettings.modifyNavigationDocument(data);
      };
    }
    return null;
  }
  private downloadDataToCache(ctx: CacheContext, item: CacheDataSet, arenaNames: string[]): Observable<CacheProgress> {
    const progress = new Subject<CacheProgress>();

    let errorCount = 0;
    let stepCount = 0;

    const currentArenaNames: string[] = [];
    if (arenaNames) {
      for (let name of arenaNames) {
        if (name && name != '') {
          currentArenaNames.push(name);
        }
      }
    }
    if (currentArenaNames.findIndex((_) => _ == ProfileInfo.defaultArenaName) < 0) {
      currentArenaNames.push(ProfileInfo.defaultArenaName);
    }

    let maxCount: number;

    ctx.storage
      .createStorageDataSet(item, {
        arenaNames: arenaNames,
      })
      .then((_) => {
        of(currentArenaNames)
          .pipe(
            concatMap(async (names) => {
              const result: [NavigationViewModel, string][] = [];
              for (let name of names) {
                const nav = await this.getNavigation(name, this.filterArenaNavigationDocument());
                await this.saveNavigationToCache(_, name, nav?.navigationDocument);
                result.push([nav, name]);
              }
              if (ctx.checkCancelled(progress)) {
                return null as [NavigationViewModel, string][];
              }
              return result;
            }),
            concatMap(async (navModels) => {
              if (!navModels) {
                return null;
              }
              const result: any[] = [];
              maxCount = 0;
              stepCount = 0;
              for (let nav of navModels) {
                const navModel = nav[0];
                if (!navModel?.hasLayouts) {
                  continue;
                }
                maxCount += Array.from(navModel.layoutsByLayoutId.keys()).length * 3;
              }

              for (let nav of navModels) {
                const navModel = nav[0];
                const name = nav[1];
                if (!navModel?.hasLayouts) {
                  progress.next(new CacheProgress(0));
                  result.push(null);
                  continue;
                }
                for (const key of Array.from(navModel.layoutsByLayoutId.keys())) {
                  try {
                    const nlyNodes = navModel.layoutsByLayoutId.get(key);
                    const layoutId = nlyNodes[0].layoutName;
                    const ly = await this.getLayout(name, layoutId);
                    await this.saveLayoutToCache(_, name, layoutId, ly.layoutObject);
                    progress.next(new CacheProgress(++stepCount / maxCount));
                    for (let filter of nlyNodes.map((n) => n.nodeFilter)) {
                      await this.saveGridDataToCache(
                        _,
                        name,
                        layoutId,
                        filter,
                        this.createGridObjectsQuery(ly, [filter], null, null),
                        ctx,
                      );
                    }
                    progress.next(new CacheProgress(++stepCount / maxCount));
                    await this.saveFormDataToCache(
                      _,
                      name,
                      layoutId,
                      this.createDataFormObjectsQueryForCaching(ly, nlyNodes, '__id'),
                      '__id',
                      ctx,
                    );
                    progress.next(new CacheProgress(++stepCount / maxCount));
                  } catch (err) {
                    progress.next(new CacheProgress(stepCount / maxCount, ++errorCount / maxCount, err.message));
                    return null;
                  }
                }
              }
              return result;
            }),
          )
          .subscribe(
            () => {
              progress.next(new CacheProgress(1.0));
            },
            (err) => {
              progress.next(
                new CacheProgress(
                  stepCount / maxCount,
                  (maxCount - stepCount) / maxCount,
                  this.abcCore.error.getFullErrorMessage(err),
                ),
              );
            },
          );
      });

    return CacheProgress.toObservable(progress);
  }

  public getStateStorageKey(arenaName: string, layoutId: string): string {
    return arenaName + '_' + layoutId;
  }

  private async saveNavigationToCache(
    item: CacheStorageDataSet,
    arenaName: string,
    navigation: NavigationDocument,
  ): Promise<void> {
    await this.saveToCache(item, [{ key: arenaName, value: navigation }], this.tableNameDefinition);
  }

  private async tryLoadNavigationFromCache(arenaName: string): Promise<any> {
    return await this.tryLoadFromCache(arenaName, this.tableNameDefinition);
  }

  private async saveLayoutToCache(
    item: CacheStorageDataSet,
    arenaName: string,
    layoutId: string,
    layout: LayoutDocument,
  ): Promise<void> {
    await this.saveToCache(
      item,
      [{ key: this.getLayoutKey(arenaName, layoutId), value: layout }],
      this.tableNameDefinition,
    );
  }

  private async tryLoadLayoutFromCache(arenaName: string, layoutId: string): Promise<any> {
    return await this.tryLoadFromCache(this.getLayoutKey(arenaName, layoutId), this.tableNameDefinition);
  }

  private async saveGridDataToCache(
    item: CacheStorageDataSet,
    arenaName: string,
    layoutId: string,
    nodeFilter: string,
    query: QueryObjectsModel,
    ctx: CacheContext,
  ): Promise<void> {
    if (this.cacheSettings && this.cacheSettings.applyExtentFilter) {
      query = await this.cacheSettings.applyExtentFilter(query, ctx);
    }
    const data = await this.readObjects(query).toPromise();
    await this.saveToCache(
      item,
      [
        {
          key: this.getGridDataKey(arenaName, layoutId, nodeFilter),
          value: data,
        },
      ],
      this.tableNameGridData,
    );
  }

  private async tryLoadGridDataFromCache(arenaName: string, layoutId: string, nodeFilter: string): Promise<any> {
    return await this.tryLoadFromCache(this.getGridDataKey(arenaName, layoutId, nodeFilter), this.tableNameGridData);
  }

  private async saveFormDataToCache(
    item: CacheStorageDataSet,
    arenaName: string,
    layoutId: string,
    query: QueryObjectsModel,
    idColumnName: string,
    ctx: CacheContext,
  ): Promise<void> {
    if (this.cacheSettings && this.cacheSettings.applyExtentFilter) {
      query = await this.cacheSettings.applyExtentFilter(query, ctx);
    }
    const data: any = await this.readObjects(query).toPromise();
    const items: { key: string; value: any }[] = [];
    if (Array.isArray(data)) {
      for (let obj of data) {
        const id: string = obj[idColumnName];
        if (id) {
          obj[idColumnName] = undefined;
          items.push({
            key: this.getObjectKey(arenaName, layoutId, id),
            value: obj,
          });
        }
      }
    }
    await this.saveToCache(item, items, this.tableNameFormData);
  }

  private async tryLoadFormDataFromCache(arenaName: string, layoutId: string, idList: string[]): Promise<any> {
    let rows = [];
    for (let id of idList) {
      const row = await this.tryLoadFromCache(this.getObjectKey(arenaName, layoutId, id), this.tableNameFormData);
      if (row) {
        rows.push(row);
      }
    }
    return rows;
  }

  private async saveToCache(
    item: CacheStorageDataSet,
    data: { key: string; value: any }[],
    tableName: string,
  ): Promise<void> {
    await item.setData(data, tableName);
  }

  private async tryLoadFromCache(key: string, tableName: string): Promise<any> {
    const ds: CacheStorageDataSet = await this.abcCore.cache.storage.getStorageDataSet(this.cacheDataSet);
    if (ds) {
      return await ds.get(key, tableName);
    }
    return null;
  }

  private getLayoutKey(arenaName: string, layoutId: string): string {
    return arenaName + '.' + layoutId;
  }

  private getGridDataKey(arenaName: string, layoutId: string, nodeFilter: string): string {
    let suffix = '';
    if (nodeFilter && nodeFilter != '') {
      suffix = '[' + nodeFilter + ']';
    }
    return this.getLayoutKey(arenaName, layoutId) + suffix;
  }

  private getObjectKey(arenaName: string, layoutId: string, id: string): string {
    return this.getLayoutKey(arenaName, layoutId) + '#' + id;
  }
}
